import type { FloorPlannerLayout, FloorRemovalRecord } from '@repo/frontend-types';
import {
  nextFloorId,
  retiredAfterLaunch,
  type FloorRestoreBlocked,
  type FloorRestoreResult,
  type IdentifiedNode,
} from '@repo/front-end-utils';

/** What putting a floor back did, for the Launch step's toast (`floor-plan-page-floors`, R19). */
export interface PutBackOutcome {
  /** `null` when there was no plan to put it back into, or it could not go back (`blocked`). */
  restored: FloorRestoreResult | null;
  /** Identities on the floor that something added since now uses; nothing was written. */
  blocked?: string[];
  /** Whether the plan, with the floor back, reached the server. */
  saved: boolean;
  /** The save's reason when it did not. */
  reason?: string;
}

/**
 * Put a floor deleted from the live twin back, from the Launch step's *Removed at Launch* list
 * (`floor-plan-page-floors`, R19): one undo step, the restore, then a save — the Floor Plan step's
 * draft saver only runs while that step is on screen, and this runs on Launch, so it saves itself.
 * Go Live already waits for a save in flight.
 *
 * The store, the undo stack and the save are passed in so the order is tested here without the
 * 7,000-line page around it.
 */
export async function putBackFloor({
  record,
  layout,
  pushUndoStep,
  restore,
  persist,
}: {
  record: FloorRemovalRecord;
  layout: FloorPlannerLayout | null | undefined;
  pushUndoStep: (layout: FloorPlannerLayout) => void;
  restore: (record: FloorRemovalRecord) => FloorRestoreResult | FloorRestoreBlocked | null;
  persist: () => Promise<{ saved: boolean; reason?: string | null }>;
}): Promise<PutBackOutcome> {
  if (!layout) return { restored: null, saved: false };
  pushUndoStep(layout);
  const restored = restore(record);
  if (!restored) return { restored: null, saved: false };
  if ('blocked' in restored) return { restored: null, saved: false, blocked: restored.blocked };
  if (restored.skipped.composites > 0 || restored.skipped.observers > 0) {
    console.warn('[floors] put back skipped', restored.skipped);
  }
  const outcome = await persist();
  const reason = 'reason' in outcome ? outcome.reason : undefined;
  return { restored, saved: outcome.saved, ...(reason ? { reason } : {}) };
}

/**
 * The layout Go Live stamps once it has launched (`floor-plan-page-floors`, R20): the activation
 * time, and no `removedFloors` — those floors are now gone from the live twin, so there is nothing
 * left to put back. The floor-id high-water mark is raised over their ids first (`nextFloorId`
 * counts them), so they are never given out again — on a plan saved before the mark existed, this
 * is the one moment their ids would otherwise be forgotten.
 */
export const activatedLayoutFor = (
  layout: FloorPlannerLayout,
  at: string,
  /**
   * The model Go Live replaced and the tree it launched (`floor-plan-page-launch-bid-changes`, R8):
   * every space and machine BID that was live and is not in the launched tree joins
   * `retiredEntityCodes`, so no mint issues it again. Optional so a caller with no model to compare
   * (none today) leaves the list as it is.
   */
  launched?: {
    live: IdentifiedNode | null | undefined;
    tree: IdentifiedNode | null | undefined;
    twinBid: string;
  },
): FloorPlannerLayout => {
  const highWater = nextFloorId(layout) - 1;
  const { removedFloors: _launchedAway, ...rest } = layout;
  /*
   * ⚠ `entityCodeChanges` is NOT cleared here. This runs when the model is replaced, BEFORE the
   * filing; a filing that then fails must be retried with the record, or the space's observers get
   * new identifiers on the retry. `withLaunchBasis` drops it, after the filing succeeds (R15).
   */
  const retired = launched
    ? retiredAfterLaunch(launched.live, launched.tree, layout.retiredEntityCodes, launched.twinBid)
    : layout.retiredEntityCodes;
  return {
    ...rest,
    activatedAt: at,
    ...(highWater > 0 ? { floorIdHighWater: highWater } : {}),
    ...(retired && retired.length > 0 ? { retiredEntityCodes: retired } : {}),
  };
};
